import {
  ComponentProps,
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';

import { useMediaQuery } from '../../theme/useMediaQuery';

import styles from './Slideshow.module.css';

interface Slide {
  key: string;
  header: ReactNode;
  body: ReactNode;
}

interface Props extends ComponentProps<'div'> {
  slides: Slide[];
  duration?: number;
  transitionDuration?: number;
  autoplay?: boolean;
  children?: never;
  panelsClassName?: string;
  tabsClassName?: string;
}

export default function Slideshow({
  slides,
  className,
  autoplay = true,
  duration = 5,
  transitionDuration = 0.3,
  'aria-label': label,
  style,
  panelsClassName,
  tabsClassName,
  ...props
}: Props) {
  const [currentSlide, setCurrentSlide] = useState<string>();
  const [currentProgress, setCurrentProgress] = useState<number>(100);
  const lastChangeTimestamp = useRef<number>(null);
  const durationMs = duration * 1000;
  const [isPaused, setIsPaused] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(`(max-width: 600px)`, false);
  const isTablet = useMediaQuery(`(max-width: 1024px)`, false);

  const onTabClicked = (e: MouseEvent<HTMLButtonElement>) => {
    const [type, key] = e.currentTarget.id.split('=', 2) || [];
    if (type !== 'tab' || !key) return;

    setCurrentSlide(key);
  };

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      const currentTarget = rootRef.current?.querySelector('[role="tab"][aria-selected="true"]') || e.currentTarget;

      const [type, key] = currentTarget.id.split('=', 2) || [];
      if (type !== 'tab' || !key) return;

      const currentIndex = slides.findIndex((slide) => slide.key === key);
      const nextElement = currentIndex + 1 >= slides.length ? slides[0] : slides[currentIndex + 1];
      const previousElement = currentIndex - 1 < 0 ? slides[slides.length - 1] : slides[currentIndex - 1];

      if (e.key === 'ArrowLeft') {
        setCurrentSlide(previousElement.key);
        rootRef.current?.querySelector<HTMLButtonElement>(`[id='tab=${previousElement.key}']`)?.focus();
      } else if (e.key === 'ArrowRight') {
        setCurrentSlide(nextElement.key);
        rootRef.current?.querySelector<HTMLButtonElement>(`[id='tab=${nextElement.key}']`)?.focus();
      }
    },
    [slides],
  );

  useEffect(() => {
    if (!autoplay || isPaused) return;

    let nextSlide = slides.findIndex(({ key }) => key === currentSlide) + 1;
    if (nextSlide >= slides.length) nextSlide = 0;

    lastChangeTimestamp.current = Date.now();
    const duration = currentProgress === 100 ? durationMs : (currentProgress * durationMs) / 100;
    const timeout = setTimeout(setCurrentSlide, duration + transitionDuration * 1000, slides[nextSlide].key);

    return () => clearTimeout(timeout);
  }, [isPaused, autoplay, slides, currentSlide, durationMs, transitionDuration, currentProgress]);

  useEffect(() => {
    if (!rootRef.current) return;

    const panelsHeight = Math.max(
      ...[...rootRef.current.querySelectorAll('[role=tabpanel]').entries()].map(([_, item]) => item.scrollHeight),
    );

    rootRef.current
      .querySelector<HTMLDivElement>('[data-panels]')
      ?.style.setProperty('min-height', `${panelsHeight}px`);
  }, [slides, isMobile, isTablet]);

  useEffect(() => {
    if (typeof currentSlide === 'undefined' && slides.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentSlide(slides[0].key);
    }
  }, [slides, currentSlide]);

  const tabs = useMemo(
    () =>
      slides.map(({ key, header }) => (
        <button
          key={key}
          id={`tab=${key}`}
          type="button"
          role="tab"
          aria-selected={key === currentSlide}
          tabIndex={key === currentSlide ? undefined : -1}
          aria-controls={`panel=${key}`}
          onClick={onTabClicked}
          onKeyDown={onKeyDown}
          className={styles.tab}
        >
          {key === currentSlide ? (
            <span
              className={styles.progress}
              role="progressbar"
              aria-label="Switching to next..."
              style={{ '--p': currentProgress } as object}
              aria-valuenow={currentProgress}
            />
          ) : (
            <span className={styles.progress} />
          )}

          {header}
        </button>
      )),
    [slides, currentSlide, currentProgress, onKeyDown],
  );

  const panels = useMemo(
    () =>
      slides.map(({ key, body }) => (
        <div
          key={key}
          id={`panel=${key}`}
          role="tabpanel"
          tabIndex={key === currentSlide ? 0 : -1}
          aria-labelledby={`tab=${key}`}
          className={clsx(styles.panel, key === currentSlide && styles.active)}
        >
          {body}
        </div>
      )),
    [slides, currentSlide],
  );

  const pauseAutoplay = () => {
    if (isPaused) return;

    const delta = (lastChangeTimestamp.current ? Date.now() - lastChangeTimestamp.current : 0) / durationMs;
    setCurrentProgress(Math.round(delta * 100));
    setIsPaused(true);
  };

  const resumeAutoplay = () => {
    if (!isPaused) return;

    setCurrentProgress(100);
    setIsPaused(false);
  };

  return (
    <div
      className={clsx(styles.container, isPaused && styles.paused, className)}
      ref={rootRef}
      style={autoplay ? ({ '--duration': duration, '--delay': transitionDuration, ...style } as object) : style}
      {...props}
    >
      <div
        role="tablist"
        aria-label={label}
        className={clsx(styles.tabs, tabsClassName)}
      >
        {tabs}
      </div>
      <div
        className={clsx(styles.panels, panelsClassName)}
        data-panels
        onMouseEnter={autoplay ? pauseAutoplay : undefined}
        onMouseLeave={autoplay ? resumeAutoplay : undefined}
      >
        {panels}
      </div>
    </div>
  );
}
